import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { PatientsService } from '../../../../../core/services/patient/patients.service';
import { PatientDetailResponse } from '../../../../../core/models/patient/patient-detail.model';
import { AiAssistantService } from '../../../../../core/services/ai/ai-assistant.service';
import {
  GetListWoundByPatientIdResponse,
  GetWoundByIdResponse,
} from 'app/core/models/wound/wound.model';
import { WoundService } from 'app/core/services/wound/wound.service';
import { BaseApiResponse } from 'app/core/models/api/api.model';
import { PatientOverviewComponent } from '../patient-overview/patient-overview.component';
import { DocumentManagerComponent } from 'app/shared/components/document-manager';
import { WoundTimelineComponent } from 'app/shared/components/wound-timeline/wound-timeline.component';
import { PermissionService } from 'app/core/services/user/permission.service';
import { ChatComponent } from 'app/main/pages/chat/chat.component';
import { Subject } from 'rxjs';
import { DietTypePipe } from 'app/pipes/diet-type.pipe';
import { SkinTypes } from 'enums/skin-types.enum';
import { LivingConditionPipe } from 'app/pipes/living-condition.pipe';

@Component({
  selector: 'app-patient-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DatePipe,
    PatientOverviewComponent,
    DocumentManagerComponent,
    WoundTimelineComponent,
    ChatComponent,
    DietTypePipe,
    LivingConditionPipe,
  ],
  templateUrl: './patient-detail.component.html',
  styleUrl: './patient-detail.component.scss',
})
export class PatientDetailComponent implements OnInit, OnDestroy {
  patientId: string = '';
  patient: PatientDetailResponse | null = null;
  isLoading = true;
  hasError = false;

  activeTab: 'chat' | 'documents' | 'upload' = 'chat';
  activeDetailTab: 'patient' = 'patient';

  private destroy$ = new Subject<void>();

  selectedWoundId: string | null = null;

  wounds: GetListWoundByPatientIdResponse[] = [];
  wound: GetWoundByIdResponse | null = null;

  doctorIds: string[] = [];

  optionsLookup = {
    maritalStatus: ['single', 'married', 'divorced', 'widow'],
    dietType: ['regular', 'vegetarian', 'vegan', 'keto', 'other'],
    skinType: ['typeI', 'typeII', 'typeIII', 'typeIV', 'typeV', 'typeVI'],
    bloodType: [
      'aPositive',
      'aNegative',
      'bPositive',
      'bNegative',
      'abPositive',
      'abNegative',
      'oPositive',
      'oNegative',
    ],
    alcoholConsumption: ['never', 'occasionally', 'frequently', 'daily'],
    smokingStatus: ['never', 'former', 'current', 'quitted'],
  };

  // ABI State
  initialAbiValue: number | null = null;
  currentAbiValue: number | null = null;
  isAbiSaving = false;
  abiEditMode = false;

  // AI Summary State
  aiSummary: string | null = null;
  isGeneratingSummary = false;
  summaryError = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private patientsService: PatientsService,
    private translate: TranslateService,
    private woundService: WoundService,
    private permissionService: PermissionService,
    private aiAssistantService: AiAssistantService,
  ) {
    const lang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(lang);
  }

  ngOnInit(): void {
    this.patientId = this.route.snapshot.paramMap.get('id') || '';
    if (this.patientId) {
      this.loadPatientData();
      this.loadWoundsData();
    } else {
      this.hasError = true;
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPatientData(): void {
    this.isLoading = true;
    this.hasError = false;

    this.patientsService.getPatientById(this.patientId).subscribe({
      next: (data) => {
        this.patient = data;
        this.initialAbiValue = data.abiValue ?? null;
        this.currentAbiValue = data.abiValue ?? null;
        this.isLoading = false;
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }

  loadWoundsData(): void {
    this.woundService.getListWoundByPatientId(this.patientId).subscribe({
      next: (data: BaseApiResponse<GetListWoundByPatientIdResponse[]>) => {
        const allWounds = data.data;
        this.wounds = allWounds;
        this.doctorIds = [];
        allWounds.forEach((wound) => {
          if (wound.followUps && wound.followUps.length > 0) {
            wound.followUps.forEach((fUp) => {
              if (fUp.woundStatus !== 4) {
                const relatedWound = allWounds.find((w) => w.id === fUp.id);
                if (relatedWound && relatedWound.doctorId) {
                  this.addDoctorId(relatedWound.doctorId);
                }
              }
            });
          } else {
            if (wound.woundStatus !== 4 && wound.doctorId) {
              this.addDoctorId(wound.doctorId);
            }
          }
        });
      },
      error: () => {
        this.wounds = [];
      },
    });
  }

  setActiveTab(tab: 'chat' | 'documents' | 'upload'): void {
    this.activeTab = tab;
  }

  getGenderText(gender: number): string {
    if (gender === 2) {
      return 'M';
    }
    if (gender === 3) {
      return 'F';
    }
    return '';
  }

  getPatientInitial(): string {
    if (!this.patient?.name) {
      return '?';
    }
    return this.patient.name.substring(0, 1).toUpperCase();
  }

  getFullName(): string {
    if (!this.patient) {
      return '';
    }
    return `${this.patient.name} ${this.patient.surname}`.trim();
  }

  get isNurse(): boolean {
    return this.permissionService.hasPermission('nurse_claim');
  }

  goBack(): void {
    this.router.navigate(['/patient/list']);
  }

  private addDoctorId(id: string): void {
    if (!this.doctorIds.includes(id)) {
      this.doctorIds.push(id);
    }
  }

  getMaritalStatusText(statusId: number): string {
    const options = ['single', 'married', 'divorced', 'widow'];
    const key = options[statusId - 1];

    return key
      ? this.translate.instant(`patientCreate.form.maritalStatusOptions.${key}`)
      : '';
  }

  getDietTypeText(id: number): string {
    const options = ['regular', 'vegetarian', 'vegan', 'keto', 'other'];
    const key = options[id - 1];
    return key
      ? this.translate.instant(
          `patientCreate.physicalLifestyle.dietTypes.${key}`,
        )
      : '';
  }

  getSkinTypeText(id: number): string {
    const options = ['typeI', 'typeII', 'typeIII', 'typeIV', 'typeV', 'typeVI'];
    const key = options[id - 1];
    return key
      ? this.translate.instant(
          `patientCreate.physicalLifestyle.skinTypes.${key}`,
        )
      : '';
  }

  getBloodTypeText(id: number): string {
    const options = [
      'aPositive',
      'aNegative',
      'bPositive',
      'bNegative',
      'abPositive',
      'abNegative',
      'oPositive',
      'oNegative',
    ];
    const key = options[id - 1];
    return key
      ? this.translate.instant(
          `patientCreate.physicalLifestyle.bloodTypes.${key}`,
        )
      : '';
  }

  getAlcoholConsumptionText(id: number): string {
    const options = ['never', 'occasionally', 'frequently', 'daily'];
    const key = options[id];
    return key !== undefined
      ? this.translate.instant(
          `patientCreate.physicalLifestyle.alcoholConsumption.${key}`,
        )
      : '';
  }

  getSmokingStatusText(id: number): string {
    const options = ['never', 'former', 'current', 'quitted'];
    const key = options[id];
    return key !== undefined
      ? this.translate.instant(
          `patientCreate.physicalLifestyle.smokingStatus.${key}`,
        )
      : '';
  }

  getLivingConditionsText(id: number): string {
    const options = [
      'none',
      'alone',
      'withFamily',
      'assistedLiving',
      'nursingHome',
    ];
    const key = options[id];

    return key !== undefined
      ? this.translate.instant(
          `patientCreate.physicalLifestyle.livingConditions.${key}`,
        )
      : '';
  }

  calculateBMI(height: number, weight: number): string {
    if (!height || !weight || height <= 0) return '-';

    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);

    return bmi.toFixed(2);
  }

  get hasAbiChanged(): boolean {
    return this.initialAbiValue !== this.currentAbiValue;
  }

  get isAbiVisible(): boolean {
    return this.currentAbiValue !== null && this.currentAbiValue !== 0;
  }

  toggleAbiOption(value: number | null): void {
    if (this.currentAbiValue === value) {
      this.currentAbiValue = null;
    } else {
      this.currentAbiValue = value;
    }
  }

  isAbiOptionSelected(value: number | null): boolean {
    return this.currentAbiValue === value;
  }

  getABIValueText(value: number | null): string {
    if (value === null || value === 0) return '';
    
    const abiOptions: { [key: number]: string } = {
      1: 'patientDetail.abi.normal',
      2: 'patientDetail.abi.borderline',
      3: 'patientDetail.abi.abnormal',
      4: 'patientDetail.abi.noncompressible',
    };

    const key = abiOptions[value];
    return key ? this.translate.instant(key) : '';
  }

  saveABIValue(): void {
    if (!this.hasAbiChanged || !this.patient) return;

    this.isAbiSaving = true;
    this.patientsService.updatePatientABI(this.patient.id, this.currentAbiValue).subscribe({
      next: () => {
        this.initialAbiValue = this.currentAbiValue;
        this.abiEditMode = false;
        this.isAbiSaving = false;
      },
      error: () => {
        this.isAbiSaving = false;
      },
    });
  }

  cancelABIChange(): void {
    this.currentAbiValue = this.initialAbiValue;
    this.abiEditMode = false;
  }

  enableABIEdit(): void {
    this.abiEditMode = !this.abiEditMode;
  }

  generateSummary(): void {
    if (!this.patient || this.isGeneratingSummary) return;

    this.isGeneratingSummary = true;
    this.summaryError = false;
    this.aiSummary = null;

    const lang = localStorage.getItem('app-lang') || 'en-US';
    const p = this.patient as any;

    const bmi = p.height && p.weight && p.height > 0
      ? parseFloat((p.weight / Math.pow(p.height / 100, 2)).toFixed(1))
      : undefined;

    const woundDtos = this.wounds.map(w => ({
      type: this.resolveWoundType(w.woundType),
      status: this.resolveWoundStatus(w.woundStatus),
      lastAssessmentDate: w.createdDateTime
        ? new Date(w.createdDateTime).toLocaleDateString('tr-TR')
        : undefined,
    }));

    this.aiAssistantService.generatePatientSummary({
      patientName: `${p.name ?? ''} ${p.surname ?? ''}`.trim() || undefined,
      age: p.age ?? undefined,
      gender: this.getGenderText(p.gender) || undefined,
      height: p.height ?? undefined,
      weight: p.weight ?? undefined,
      bmi,
      maritalStatus: p.maritalStatus ? this.getMaritalStatusText(p.maritalStatus) : undefined,
      bloodType: p.bloodType ? this.getBloodTypeText(p.bloodType) : undefined,
      skinType: p.skinType ? this.getSkinTypeText(p.skinType) : undefined,
      dietType: p.dietType ? this.getDietTypeText(p.dietType) : undefined,
      smokingStatus: p.smokingInformation?.smokingStatus != null
        ? this.getSmokingStatusText(p.smokingInformation.smokingStatus) : undefined,
      alcoholConsumption: p.alcoholConsumption?.type != null
        ? this.getAlcoholConsumptionText(p.alcoholConsumption.type) : undefined,
      livingConditions: p.livingConditions != null
        ? this.getLivingConditionsText(p.livingConditions) : undefined,
      placeOfResidence: p.placeOfResidence ?? undefined,
      abiValue: this.getABIValueText(this.initialAbiValue) || undefined,
      medications: this.patient.medicationDto?.map(m => m.name),
      antibiotics: this.patient.antibioticDto?.map(a => a.name),
      allergies: this.patient.allergyDto?.map(a => a.name),
      surgeries: this.patient.surgeryDto?.map(s => s.name),
      debridements: this.patient.debridementDto?.map(d => d.name),
      woundCareProducts: this.patient.woundCareProductDto?.map(w => w.name),
      wounds: woundDtos,
      language: lang,
    }).subscribe({
      next: (res) => {
        this.aiSummary = res.summary;
        this.isGeneratingSummary = false;
      },
      error: () => {
        this.summaryError = true;
        this.isGeneratingSummary = false;
      },
    });
  }

  private resolveWoundType(woundType: number): string {
    const types: Record<number, string> = {
      0: 'Belirtilmemiş', 1: 'Basınç yarası', 2: 'Diyabetik ülser',
      3: 'Venöz ülser', 4: 'Arteriyel ülser', 5: 'Cerrahi yara', 6: 'Travmatik yara',
    };
    return types[woundType] ?? `Tip ${woundType}`;
  }

  private resolveWoundStatus(status: number): string {
    const statuses: Record<number, string> = {
      0: 'Değerlendirilmedi', 1: 'Aktif', 2: 'İyileşiyor', 3: 'İyileşti', 4: 'Kronik',
    };
    return statuses[status] ?? `Durum ${status}`;
  }
}
